// Internal navigation URL helpers. More helpers (profile, conversation, ...) land here next.

//
// V2 requests live at `/requests/:slug`. V1 (vested-rails) has no such route —
// the equivalent V1 page is `/source_requests/:id` where the id is the V1
// numeric primary key. V1 exposes this id as `migrate_id` on every record that
// was migrated from V1, and V2's API views pass it through.
//
// `requestUrl` branches at runtime so the same React tree works in both apps:
//   - On V2: `globalThis.Qwoted.V2_URL` is not set (V2 has no concept of its
//     own base URL in this global). Build the V2 path using the slug.
//   - On V1: `globalThis.Qwoted.V2_URL` is set by V1's config script (it points
//     at V2). Build the V1 path using `migrate_id`.
//
// Accepts either a full request object (with `slug` and `migrate_id`) or any
// object shape with those two fields — so call sites with a denormalized
// `pitch.request_slug` / `pitch.request_migrate_id` pair can build an object
// literal inline.
export function requestUrl({ slug, migrate_id: migrateId }) {
  // On V1 (V2_URL is set): prefer the V1 source_request id when known.
  // If the pitch's request was created V2-native (no migrate_id), V1 has no
  // local record, so link absolutely to V2.
  if (globalThis.Qwoted?.V2_URL) {
    if (migrateId) {
      return `/source_requests/${migrateId}`;
    }
    return `${globalThis.Qwoted.V2_URL}/requests/${slug}`;
  }
  return `/requests/${slug}`;
}

//
// V2 profiles live at `/profiles/:slug`. V1 splits this across `/reporters/:slug`,
// `/pr_users/:slug`, and `/sources/:slug`, with a `Frontend::ProfilesController`
// resolver mounted at `/profiles/:id` that redirects to the right one. The
// resolver accepts both V1 user slugs and V1 user ids — passing the numeric
// `user_migrate_id` (= V1 user id) skips the slug-mismatch + PostgREST hop.
//
// Same shape as `requestUrl`:
//   - V2: build the V2 path using the slug.
//   - V1 (V2_URL set): prefer `user_migrate_id` for fast id-based resolution;
//     fall back to the represented-expert branch (`/sources/:migrate_id`) for
//     participants who are V1 sources with no V1 user account of their own —
//     the inbox sidebar promotes `origin_expert` (which already carries the V1
//     source `migrate_id` via PR #823) into the participant list, so this
//     helper sees it for free. Finally fall back to absolute V2 URL when the
//     participant is V2-native (no V1 trace at all).
//
export function profileUrl({ slug, user_migrate_id: userMigrateId, migrate_id: migrateId }) {
  if (globalThis.Qwoted?.V2_URL) {
    if (userMigrateId) {
      return `/profiles/${userMigrateId}`;
    }
    if (migrateId) {
      return `/sources/${migrateId}`;
    }
    return `${globalThis.Qwoted.V2_URL}/profiles/${slug}`;
  }
  return `/profiles/${slug}`;
}

//
// Experts live at `/experts/:slug` on V2; the V1 equivalent is `/sources/:id`, where the id is the
// V1 source id (the expert's `migrate_id`). Same branching as profileUrl: on V1 (V2_URL set) prefer
// the V1-local `/sources/:migrate_id`, falling back to absolute V2 for V2-native experts.
//
export function expertUrl({ slug, migrate_id: migrateId }) {
  if (globalThis.Qwoted?.V2_URL) {
    if (migrateId) {
      return `/sources/${migrateId}`;
    }
    return `${globalThis.Qwoted.V2_URL}/experts/${slug}`;
  }
  return `/experts/${slug}`;
}

//
// Products live at `/products/:slug` on V2 and `/products/:id` on V1 (V1's products controller is
// id-based, where id is the V1 product id = V2's `migrate_id`). Same V2_URL branching pattern as
// expertUrl: on V1 prefer the V1-local path when we have a migrate_id, absolute V2 fallback for
// V2-native products.
//
export function productUrl({ slug, migrate_id: migrateId }) {
  if (globalThis.Qwoted?.V2_URL) {
    if (migrateId) {
      return `/products/${migrateId}`;
    }
    return `${globalThis.Qwoted.V2_URL}/products/${slug}`;
  }
  return `/products/${slug}`;
}

//
// Companies live at `/companies/:slug` on V2 and `/companies/:id` on V1 (V1's companies controller
// is id-based, where id is the V1 company id = V2's `migrate_id`). Same V2_URL branching pattern as
// expertUrl/productUrl.
//
export function companyUrl({ slug, migrate_id: migrateId }) {
  if (globalThis.Qwoted?.V2_URL) {
    if (migrateId) {
      return `/companies/${migrateId}`;
    }
    return `${globalThis.Qwoted.V2_URL}/companies/${slug}`;
  }
  return `/companies/${slug}`;
}

//
// PR profiles live at `/pr_profiles/:slug` on V2 and `/pr_users/:id` on V1 (V1's PrUsersController is
// id-based, where id is the V1 PrUser id = V2's `migrate_id` for a Promoter profile). Same
// V2_URL branching pattern as expertUrl/productUrl/companyUrl.
//
export function prProfileUrl({ slug, migrate_id: migrateId }) {
  if (globalThis.Qwoted?.V2_URL) {
    if (migrateId) {
      return `/pr_users/${migrateId}`;
    }
    return `${globalThis.Qwoted.V2_URL}/pr_profiles/${slug}`;
  }
  return `/pr_profiles/${slug}`;
}

//
// Media profiles live at `/media_profiles/:slug` on V2. V1 has no `media_profiles` route; the
// equivalent V1 page is `/reporters/:id`, resolved by `Reporter.friendly.find` — the Reporter's OWN
// id, not the V1 user id of the account that manages it. Every path that creates a V2 MediaProfile
// (the offline migration, the on-demand Kafka listener, and the reconciler) sets
// `migrate_id = v1_reporter.id` / `migrate_type = "Reporter"`, so `migrate_id` here is already the
// right id for this route — same V2_URL branching pattern as expertUrl/productUrl.
//
export function mediaProfileUrl({ slug, migrate_id: migrateId }) {
  if (globalThis.Qwoted?.V2_URL) {
    if (migrateId) {
      return `/reporters/${migrateId}`;
    }
    return `${globalThis.Qwoted.V2_URL}/media_profiles/${slug}`;
  }
  return `/media_profiles/${slug}`;
}

//
// Media outlets live at `/media_outlets/:slug` on V2. V1 has no `media_outlets` route; the
// equivalent V1 page is `/publications/:id`, resolved by `Publication.friendly.find` — the
// Publication's own id. The V1↔V2 reconciler and every MediaOutlet creation path set
// `migrate_id = v1_publication.id` (`p.id = mo.migrate_id`), so `migrate_id` here is already the
// right id for this route — same V2_URL branching pattern as expertUrl/productUrl.
//
export function mediaOutletUrl({ slug, migrate_id: migrateId }) {
  if (globalThis.Qwoted?.V2_URL) {
    if (migrateId) {
      return `/publications/${migrateId}`;
    }
    return `${globalThis.Qwoted.V2_URL}/media_outlets/${slug}`;
  }
  return `/media_outlets/${slug}`;
}

//
// The entity URL helpers above return a path relative to whichever app is currently serving the
// page, EXCEPT their V2-native fallback, which is already an absolute URL pointing at V2. A
// canonical/share link (copied to the clipboard, or printed as a `<link rel="canonical">`) needs a
// full URL every time, not just in the fallback case, so this adds the current origin unless the
// helper already returned an absolute one.
//
export function toAbsoluteUrl(path) {
  return /^https?:\/\//.test(path) ? path : `${window.location.origin}${path}`;
}
